// The Exchange: a labelled house trader that seeds the coin market at launch (research:
// mythic-idle-design/market-bots-research.md). Pure rules, shared by the server (server/exchange.ts),
// the market screen, the Admin page and the sim (sim/exchange-sim.ts).
//
// - Coins only. It is one named seller ("The Exchange") on the market, never a player: not on Ranks,
//   not in chat, not counted as an account.
// - Per item it carries, it sells a daily stock at the ask (reference × sellMult) as a normal listing,
//   and buys up to a daily budget at the bid (reference × buyMult). Players who undercut its ask or
//   outbid its bid win the trade, so players set the price inside the band and the Exchange only
//   catches the extremes.
// - Selling to it pays the normal 15% market fee, so it is never better than selling to a player at the
//   same price. What it buys it resells (its inventory goes on its listing next to the day's stock, and
//   sells first), so buying never takes supply away from players; past INV_CAP_DAYS of budget the rest
//   is destroyed. Only the daily stock is new. Its coins come from nowhere and go nowhere: buys mint,
//   sales burn, and the gap between bid and ask makes it a net burn.
// - Demand pricing, once a day: stock sold out with buyers left over moves the reference up, budget
//   used up with stock left over moves it down, and real player sales (the price table counts only
//   those) pull it toward their 7-day average. At most ±MAX_DAY_MOVE a day, inside the item's min/max.
// - Taper: once players trade an item TAPER_RATIO times as much as the Exchange does for a week, its
//   stock and budget halve and the band widens; after TAPER_STAGES halvings it stops carrying the item.
// - Supply gate: an item nobody can find yet at launch (Embers, Dragon Pearls, the later boss keys, Boss
//   Ladder fragments, Awakening Shards and Stones) gets no daily stock until players have sold GATE_UNITS
//   of it among themselves in the last 7 days. Until then it only buys and resells what players bring,
//   so the house never puts an item on the market faster than the game drops it.
// - Never carries rares (Aura stones and shards, shrines, creatures, relics).
// - Launch window (Cole 2026-10-20: the Exchange only gets the market going for the first month, it is not meant to
//   be a big part of the game): full stock and budget for LAUNCH_DAYS after the launch day an admin sets (the same one
//   that opens tournaments, server/tournaments.ts), then both fade to nothing over FADE_DAYS and it closes. With no
//   launch day set it runs in full.
import { ITEM, STONES, AURA_AWAKENING_SHARD, AWAKENING_SHARD, awakeningStone } from './items.ts';
import { AWAKENINGS } from './awakening-data.ts';

/** The listings seller id of the Exchange (no account has id 0). */
export const EXCHANGE_SELLER = 0;
export const EXCHANGE_NAME = 'The Exchange';
export const DAY_MS = 86_400_000;

export const EXCHANGE = {
  buyMult: 0.6,
  sellMult: 1.25,
  /** Daily reference move from its own fills (sold out / bought out). */
  demandStep: 0.1,
  /** Share of the gap to the players' 7-day average closed each day. */
  anchorWeight: 0.3,
  /** Player units in 7 days before their average counts. */
  anchorMinUnits: 10,
  /** Largest reference change in one day. */
  maxDayMove: 0.15,
  /** A side counts as "out" at this share used. */
  fullShare: 0.9,
  /** One account may use at most this share of an item's daily budget. */
  accountShare: 0.25,
  /** Taper when player units over 7 days reach this many times the Exchange's own. */
  taperRatio: 10,
  /** ... and at least this many player units. */
  taperMinUnits: 50,
  /** Halvings before it stops carrying an item. */
  taperStages: 3,
  /** Band widening per taper stage. */
  taperBuyWiden: 0.1,
  taperSellWiden: 0.15,
  /** Days of history kept (taper and Admin). */
  histDays: 7,
  /** Inventory kept for resale, in days of buy budget; the rest is destroyed. */
  invCapDays: 3,
  /** Supply gate: player units over 7 days before a gated item gets daily stock. */
  gateUnits: 50,
  /** Launch window: days after launch day at full stock and budget, then days fading to nothing (launchShare). */
  launchDays: 30,
  fadeDays: 7,
} as const;

/**
 * Retired (Cole 2026-10-20): players stock the market themselves. Everything the Exchange carried also drops in play,
 * and its daily stock was new supply from nothing (20 Fenrir keys and 50 of each evolution stone a day). The live
 * server passes this to server/exchange.ts, which then lists nothing, buys nothing and ends its open listings; the
 * rules stay for the sims and tests.
 */
export const EXCHANGE_RETIRED = true;

export interface ExchangeItemCfg {
  /** Carried at all (Admin toggle). */
  on: boolean;
  /** Reference price per unit, coins. */
  ref: number;
  /** Units it sells a day, and buys a day, before taper. */
  stock: number;
  budget: number;
  /** Bounds on the reference. */
  min: number;
  max: number;
  /** Demand pricing on (off holds the reference where Admin set it). */
  auto: boolean;
  /** Taper stage, 0 to EXCHANGE.taperStages (the last means stopped). */
  stage: number;
  /**
   * Supply gate: no daily stock until players have sold this many units among themselves in the last 7 days
   * (0 or absent: stock from the start). It still buys, and resells what it bought, while the gate is shut.
   */
  gate?: number;
  /** The gate is open: set at each day roll from the players' last 7 days. */
  open?: boolean;
}

export interface DayCount {
  bought: number;
  sold: number;
  /** Of sold, units that were new (the daily stock) rather than resold inventory. */
  made?: number;
}

export interface ExchangeState {
  on: boolean;
  items: Record<string, ExchangeItemCfg>;
  /** Day index (UTC days since epoch) the counts belong to. */
  day: number;
  /** Today's units per item. */
  today: Record<string, DayCount>;
  /** Units bought from players and not yet resold, per item. */
  inv: Record<string, number>;
  /** Today's units per item per account sold to the Exchange (the per-account share). */
  acct: Record<string, Record<string, number>>;
  /** Past days, newest first, at most EXCHANGE.histDays. */
  hist: { day: number; counts: Record<string, DayCount>; refs: Record<string, number> }[];
}

/**
 * What it carries at launch and the launch reference prices. The prices are guesses from the economy
 * sim (coins per player-day over drops per player-day, roughly) and fix themselves through demand
 * pricing; Admin can set any of them. Stock and budget are a few percent of a 1,000-player server's
 * daily drops, so the Exchange never floods an item.
 */
const DEFAULTS: [id: string, ref: number, stock: number, budget: number][] = [
  // Evolution stones (one per element; Cole wants them buyable). Pokeidle's trade at 40k-140k.
  ...STONES.map((id): [string, number, number, number] => [id, 100_000, 50, 50]),
  ['rune-of-ascent', 200_000, 200, 200],
  ['olympian-ember', 1_000_000, 100, 100],
  ['dragon-pearl', 10_000_000, 5, 5],
  ['fenrir-token', 3_000_000, 20, 20],
  ['typhon-token', 15_000_000, 8, 8],
  ['hachimata-token', 60_000_000, 3, 3],
  ['tablet-fragment', 500_000, 20, 20],
  ['fenrir-crest-shard', 500_000, 20, 20],
  ['typhon-crest-shard', 500_000, 20, 20],
  ['hachimata-crest-shard', 500_000, 20, 20],
  [AWAKENING_SHARD, 40_000_000, 5, 5],
  // Cole wants Awakening Stones buyable from it (2026-10-14). They sit behind the supply gate: it resells the stones players
  // sell it and adds none of its own, since one a day per species was about seven times what boss wins make (audit 2026-10-20).
  ...AWAKENINGS.map((a): [string, number, number, number] => [awakeningStone(a.from), 500_000_000, 1, 1]),
];

/**
 * Items behind the supply gate: nothing drops them at launch, or only a trickle, so a daily stock from day one
 * would be most of the server's supply (economy projection 2026-10-20 at 1,000 daily players: no Dragon Pearl or
 * Sacred Rope dropped by day 90, no Embers before Hellas's star hunts at Lv 550, no Boss Ladder fragments before
 * Lv 3,000, and boss wins make about a seventh of the 45 awakenings a week its old stock added).
 */
const GATED = new Set<string>(['olympian-ember', 'dragon-pearl', 'typhon-token', 'hachimata-token', 'tablet-fragment', 'fenrir-crest-shard', 'typhon-crest-shard', 'hachimata-crest-shard', AWAKENING_SHARD, ...AWAKENINGS.map((a) => awakeningStone(a.from))]);
/** The supply gate an item starts with (0: none). */
export const launchGate = (id: string) => (GATED.has(id) ? EXCHANGE.gateUnits : 0);
/** The gate holds back the daily stock. */
export const gateShut = (c: Pick<ExchangeItemCfg, 'gate' | 'open'>) => (c.gate ?? 0) > 0 && !c.open;
/** Whether the gate opens on a week of this many player units. */
export const gateOpens = (c: Pick<ExchangeItemCfg, 'gate'>, playerUnits7: number) => playerUnits7 >= (c.gate ?? 0);
/**
 * The day roll's gate update: open or shut from the players' last 7 days. A gate that opens sets the reference to the
 * players' 7-day average (at least gateUnits units of it), inside min and max: while shut, with few player trades to
 * anchor to, the reference can sit for weeks while coins inflate, and the first stock must not sell at that price.
 */
export function rollGate(c: ExchangeItemCfg, playerUnits7: number, playerAvg7: number | null) {
  if (!c.gate) return;
  const was = !!c.open;
  c.open = gateOpens(c, playerUnits7);
  if (c.open && !was && playerAvg7 !== null) c.ref = Math.round(Math.min(c.max, Math.max(c.min, playerAvg7)));
}

/**
 * Never carried, whatever Admin sets: rare outcomes stay player-made, and odds charms (Aura and Catch Charms, kind
 * 'boost') come only from PvP (Cole, 2026-10-15), so the house never sells or buys them.
 */
export function exchangeBanned(id: string): boolean {
  const it = ITEM[id];
  if (!it || !it.tradable) return true;
  if (id === AURA_AWAKENING_SHARD || id.startsWith('aura-')) return true;
  return it.kind === 'shard' || it.kind === 'boost';
}

export function defaultItemCfg(id: string, ref = Math.max(100, (ITEM[id]?.sellPrice ?? 0) * 10), stock = 10, budget = 10): ExchangeItemCfg {
  const floor = Math.max(10, Math.ceil(((ITEM[id]?.sellPrice ?? 0) * 1.5) / EXCHANGE.buyMult));
  const gate = launchGate(id);
  return { on: true, ref: Math.max(ref, floor), stock, budget, min: Math.max(floor, Math.floor(ref / 20)), max: ref * 50, auto: true, stage: 0, ...(gate ? { gate } : {}) };
}

export function defaultExchange(now: number): ExchangeState {
  const items: Record<string, ExchangeItemCfg> = {};
  for (const [id, ref, stock, budget] of DEFAULTS) if (!exchangeBanned(id)) items[id] = defaultItemCfg(id, ref, stock, budget);
  return { on: true, items, day: dayOf(now), today: {}, inv: {}, acct: {}, hist: [] };
}

export const dayOf = (now: number) => Math.floor(now / DAY_MS);

/**
 * The share of its stock and budget the Exchange runs at on UTC day `day` (dayOf): 1 with no launch day set and for
 * EXCHANGE.launchDays after it, then falling evenly to 0 over EXCHANGE.fadeDays; 0 means closed. Set once a day, so a
 * day's caps never move under its buyers.
 */
export function launchShare(launch: number | null | undefined, day: number): number {
  if (launch == null) return 1;
  const past = day - dayOf(launch) - EXCHANGE.launchDays;
  return past < 0 ? 1 : Math.max(0, 1 - (past + 1) / (EXCHANGE.fadeDays + 1));
}

/** The first UTC day the Exchange is closed for a launch day (null: no launch day, no closing). */
export const exchangeClosesDay = (launch: number | null | undefined): number | null => (launch == null ? null : dayOf(launch) + EXCHANGE.launchDays + EXCHANGE.fadeDays);

/** Effective daily stock and budget after taper and the launch window (`share`); no stock while the supply gate is shut. */
export function caps(c: ExchangeItemCfg, share = 1): { stock: number; budget: number } {
  if (!c.on || c.stage >= EXCHANGE.taperStages || share <= 0) return { stock: 0, budget: 0 };
  const k = 0.5 ** c.stage * Math.min(1, share);
  return { stock: gateShut(c) ? 0 : Math.floor(c.stock * k), budget: Math.floor(c.budget * k) };
}

/** Units it may still sell today: resold inventory plus what is left of the new stock. */
export function sellLeft(c: ExchangeItemCfg, today: DayCount | undefined, inv: number, share = 1): number {
  const cap = caps(c, share);
  if (!cap.stock && !cap.budget) return 0;
  return Math.max(0, cap.stock - (today?.made ?? 0)) + Math.max(0, inv);
}

/** Split a sale of n units into resold inventory first, then new stock. */
export function splitSale(inv: number, n: number): { fromInv: number; made: number } {
  const fromInv = Math.min(Math.max(0, inv), n);
  return { fromInv, made: n - fromInv };
}

/** Bid and ask per unit after taper widening. The bid never reaches the ask. */
export function quotes(c: ExchangeItemCfg): { bid: number; ask: number } {
  const bid = Math.max(1, Math.floor(c.ref * (EXCHANGE.buyMult - EXCHANGE.taperBuyWiden * c.stage)));
  const ask = Math.max(bid + 1, Math.ceil(c.ref * (EXCHANGE.sellMult + EXCHANGE.taperSellWiden * c.stage)));
  return { bid, ask };
}

/**
 * Tomorrow's reference from today's fills and the players' 7-day average (null when they traded too
 * little). Fills: sold/stock and bought/budget, 0-1.
 */
/**
 * A day's sell fill for demand pricing: units sold against all it offered, the new stock plus the inventory it
 * started the day with. With no stock of its own (a shut supply gate) it only resells what players bring, so the
 * units it bought that day count as offered too: buying one and reselling it is a balanced day, not one where
 * nothing sold (which, with the buy side full, would walk the price down day after day).
 */
export function sellFill(stock: number, invStart: number, sold: number, bought: number): number {
  const offered = stock + invStart + (stock ? 0 : bought);
  return offered > 0 ? Math.min(1, sold / offered) : 0;
}

export function reprice(c: ExchangeItemCfg, sellFill: number, buyFill: number, playerAvg: number | null, playerUnits: number): number {
  if (!c.auto) return c.ref;
  let next = c.ref;
  const full = EXCHANGE.fullShare;
  if (sellFill >= full && buyFill < 0.5) next *= 1 + EXCHANGE.demandStep;
  else if (buyFill >= full && sellFill < 0.5) next *= 1 - EXCHANGE.demandStep;
  if (playerAvg !== null && playerUnits >= EXCHANGE.anchorMinUnits) next += (playerAvg - next) * EXCHANGE.anchorWeight;
  const lo = c.ref * (1 - EXCHANGE.maxDayMove), hi = c.ref * (1 + EXCHANGE.maxDayMove);
  next = Math.min(hi, Math.max(lo, next));
  return Math.round(Math.min(c.max, Math.max(c.min, next)));
}

/** The next taper stage from a week's player units against the Exchange's own (bought + sold). */
export function taper(c: ExchangeItemCfg, playerUnits7: number, exchangeUnits7: number): number {
  if (c.stage >= EXCHANGE.taperStages) return c.stage;
  if (playerUnits7 >= EXCHANGE.taperMinUnits && playerUnits7 >= EXCHANGE.taperRatio * Math.max(1, exchangeUnits7)) return c.stage + 1;
  return c.stage;
}

/** Units one account may still sell to the Exchange today. */
export function accountLeft(budget: number, used: number): number {
  return Math.max(0, Math.max(1, Math.ceil(budget * EXCHANGE.accountShare)) - used);
}

/** One carried item as the market screen sees it. */
export interface ExchangeOffer {
  id: string;
  bid: number;
  ask: number;
  /** Units it still buys today, and that this player may still sell it. */
  buyLeft: number;
  mineLeft: number;
  /** Units it still sells today (also its listing). */
  sellLeft: number;
}

export interface ExchangeDTO { offers: ExchangeOffer[]; resetAt: number }

/** Admin view: config, today's numbers and the week. */
export interface ExchangeAdminItem extends ExchangeItemCfg {
  id: string;
  bid: number;
  ask: number;
  today: DayCount;
  /** Units held for resale. */
  inv: number;
  caps: { stock: number; budget: number };
  /** Units in the last 7 days (including today): players among themselves, and the Exchange. */
  player7: number;
  exchange7: number;
  playerAvg7: number | null;
}

export interface ExchangeAdmin {
  on: boolean;
  /** EXCHANGE_RETIRED on this server: nothing trades whatever the switches say. */
  retired?: boolean;
  items: ExchangeAdminItem[];
  /** Coins over 7 days: minted paying players, burned from its sales. */
  minted7: number;
  burned7: number;
  /** Accounts that traded most with it over 7 days (coins both ways). */
  top: { who: string; coins: number; trades: number }[];
  resetAt: number;
  /** Launch window: today's share of stock and budget (1 full, 0 closed) and the first closed day's start, if a launch day is set. */
  share: number;
  closesAt: number | null;
}

/** Admin's settable fields, validated server-side. */
export type ExchangeEdit = Partial<Pick<ExchangeItemCfg, 'on' | 'ref' | 'stock' | 'budget' | 'min' | 'max' | 'auto' | 'stage' | 'gate'>>;
